import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Users, CreditCard, CheckCircle, UserCheck, Loader2, RefreshCw, Zap, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  isPaymentBotConfigured,
  getPaymentBotStats,
  getAllPaymentBotConnections,
  disconnectPaymentBotUser,
  getUserPaymentBotConnection,
  generatePaymentBotConnectionCode
} from '@/services/telegram/paymentNotificationService';
import { supabase } from '@/integrations/supabase/client';

interface PaymentBotConnection {
  id: string;
  user_id: string;
  telegram_chat_id: number;
  telegram_username: string | null;
  is_active: boolean;
  created_at: string;
  user_profiles: {
    first_name: string | null;
    last_name: string | null;
    role: string | null;
  } | null;
}

const PaymentBotManager: React.FC = () => {
  const { toast } = useToast();
  const { user, userRole } = useAuth();
  const [isConfigured, setIsConfigured] = useState<boolean | null>(null);
  const [stats, setStats] = useState({ total: 0, active: 0 });
  const [connections, setConnections] = useState<PaymentBotConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [userConnection, setUserConnection] = useState<any>(null);
  const [connectionCode, setConnectionCode] = useState<string | null>(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [isSettingWebhook, setIsSettingWebhook] = useState(false);
  const [webhookInfo, setWebhookInfo] = useState<any>(null);

  const isAdmin = userRole === 'admin';

  const checkConfiguration = async () => {
    try {
      const configured = await isPaymentBotConfigured();
      setIsConfigured(configured);
      
      if (configured) {
        const [statsData, connectionsData] = await Promise.all([
          getPaymentBotStats(),
          isAdmin ? getAllPaymentBotConnections() : Promise.resolve([]),
        ]);
        
        setStats(statsData);
        setConnections(connectionsData as PaymentBotConnection[]);

        // Check user's own connection
        if (user?.id) {
          const userConn = await getUserPaymentBotConnection(user.id);
          setUserConnection(userConn);
        }

        // Get webhook info for admins
        if (isAdmin) {
          try {
            const { data } = await supabase.functions.invoke('telegram-payment-bot', {
              body: { action: 'get-webhook-info' }
            });
            setWebhookInfo(data);
          } catch (e) {
            console.error('Error getting webhook info:', e);
          }
        }
      }
    } catch (error) {
      console.error('Error checking payment bot configuration:', error);
      setIsConfigured(false);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    try {
      const { data, error } = await supabase.functions.invoke('telegram-payment-bot', {
        body: { action: 'test-connection' }
      });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: "Connection Successful",
          description: data.message,
        });
        setIsConfigured(true);
      } else {
        toast({
          variant: "destructive",
          title: "Connection Failed",
          description: data?.message || "Could not connect to payment bot",
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to test connection",
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleSetupWebhook = async () => {
    setIsSettingWebhook(true);
    try {
      const { data, error } = await supabase.functions.invoke('telegram-payment-bot', {
        body: { action: 'setup-webhook', command: 'set' }
      });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: "Webhook Set",
          description: `Webhook URL: ${data.url}`,
        });
        setWebhookInfo({ ...webhookInfo, url: data.url, success: true });
      } else {
        toast({
          variant: "destructive",
          title: "Failed to Set Webhook",
          description: data?.error || "Unknown error",
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to setup webhook",
      });
    } finally {
      setIsSettingWebhook(false);
    }
  };

  const handleGenerateCode = async () => {
    if (!user?.id) return;
    
    setIsGeneratingCode(true);
    try {
      const result = await generatePaymentBotConnectionCode(user.id);
      if (result) {
        setConnectionCode(result.code);
        toast({
          title: "Connection Code Generated",
          description: "Use this code in the Telegram bot to connect your account.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to generate connection code",
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to generate code",
      });
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const handleDisconnectUser = async (userId: string) => {
    try {
      const result = await disconnectPaymentBotUser(userId);
      if (result.success) {
        toast({
          title: "User Disconnected",
          description: "The user has been disconnected from the payment bot.",
        });
        checkConfiguration();
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: result.error || "Failed to disconnect user",
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to disconnect user",
      });
    }
  };

  const handleDisconnectSelf = async () => {
    if (!user?.id) return;
    
    try {
      const result = await disconnectPaymentBotUser(user.id);
      if (result.success) {
        toast({
          title: "Disconnected",
          description: "You have been disconnected from the payment bot.",
        });
        setUserConnection(null);
        checkConfiguration();
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: result.error || "Failed to disconnect",
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to disconnect",
      });
    }
  };

  useEffect(() => {
    checkConfiguration();
  }, [user?.id]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Bot Manager
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Bot Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Bot
          </CardTitle>
          <CardDescription>
            Telegram bot for payment request notifications and approvals.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isConfigured ? (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                The payment bot is configured and ready to use.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant="destructive">
              <AlertDescription>
                The payment bot is not configured. Please add the TELEGRAM_PAYMENT_BOT_TOKEN secret.
              </AlertDescription>
            </Alert>
          )}

          {isAdmin && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleTestConnection}
                disabled={isTestingConnection}
              >
                {isTestingConnection ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Test Connection
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSetupWebhook}
                disabled={isSettingWebhook || !isConfigured}
              >
                {isSettingWebhook ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Zap className="h-4 w-4 mr-2" />
                )}
                Setup Webhook
              </Button>
            </div>
          )}

          {isAdmin && webhookInfo && (
            <div className="text-sm text-muted-foreground">
              <p><strong>Webhook URL:</strong> {webhookInfo.url || 'Not set'}</p>
              {webhookInfo.pending_update_count !== undefined && (
                <p><strong>Pending Updates:</strong> {webhookInfo.pending_update_count}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Connection Card */}
      <Card>
        <CardHeader>
          <CardTitle>Your Connection</CardTitle>
          <CardDescription>
            Connect your Telegram account to receive payment notifications.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {userConnection ? (
            <div className="space-y-4">
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription className="flex items-center justify-between">
                  <span>
                    Connected as @{userConnection.telegram_username || 'Unknown'} 
                    (since {new Date(userConnection.created_at).toLocaleDateString()})
                  </span>
                  <Button variant="outline" size="sm" onClick={handleDisconnectSelf}>
                    Disconnect
                  </Button>
                </AlertDescription>
              </Alert>
              <p className="text-sm text-muted-foreground">
                You will receive notifications for payment request approvals, rejections, and confirmations.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Connect your Telegram account to receive payment notifications.
              </p>
              
              {connectionCode ? (
                <div className="space-y-3">
                  <div className="p-4 bg-muted rounded-lg text-center">
                    <p className="text-sm text-muted-foreground mb-2">Your connection code:</p>
                    <p className="text-2xl font-mono font-bold tracking-wider">{connectionCode}</p>
                    <p className="text-xs text-muted-foreground mt-2">Valid for 24 hours</p>
                  </div>
                  <div className="text-sm space-y-1">
                    <p>1. Open Telegram and search for the Payment Bot</p>
                    <p>2. Start a conversation and send: <code className="bg-muted px-1 rounded">/connect {connectionCode}</code></p>
                    <p>3. Your account will be linked automatically</p>
                  </div>
                  <Button variant="outline" onClick={() => setConnectionCode(null)}>
                    Generate New Code
                  </Button>
                </div>
              ) : (
                <Button onClick={handleGenerateCode} disabled={isGeneratingCode || !isConfigured}>
                  {isGeneratingCode ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4 mr-2" />
                  )}
                  Generate Connection Code
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics - Admin Only */}
      {isAdmin && isConfigured && (
        <Card>
          <CardHeader>
            <CardTitle>Connection Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-muted rounded-lg">
                <Users className="h-8 w-8 mx-auto text-primary mb-2" />
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-sm text-muted-foreground">Total Connections</div>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                <CheckCircle className="h-8 w-8 mx-auto text-green-600 mb-2" />
                <div className="text-2xl font-bold">{stats.active}</div>
                <div className="text-sm text-muted-foreground">Active</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Connected Users List - Admin Only */}
      {isAdmin && isConfigured && (
        <Card>
          <CardHeader>
            <CardTitle>Connected Users</CardTitle>
            <CardDescription>
              Users who have connected to the payment bot
            </CardDescription>
          </CardHeader>
          <CardContent>
            {connections.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No users have connected to the payment bot yet.
              </div>
            ) : (
              <div className="space-y-4">
                {connections.map((connection) => (
                  <div
                    key={connection.id}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <UserCheck className="h-5 w-5 text-green-600" />
                      <div>
                        <div className="font-medium">
                          {connection.user_profiles?.first_name} {connection.user_profiles?.last_name}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-4">
                          {connection.telegram_username && (
                            <span>@{connection.telegram_username}</span>
                          )}
                          <Badge variant="outline">
                            {connection.user_profiles?.role || 'user'}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Connected: {new Date(connection.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={connection.is_active ? 'default' : 'secondary'}>
                        {connection.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDisconnectUser(connection.user_id)}
                      >
                        Disconnect
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PaymentBotManager;
